import { format } from "date-fns"

import { segmentLoadStates } from "@/lib/routeCostCalculation"
import { getRouteKindLaneClasses } from "@/lib/routeKindDisplay"
import { ROW_HEIGHT, timeToPosition } from "@/lib/timelineScale"
import { type Variance, varianceClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type { Route, Stop } from "@/types/collections"

import TimelineStop from "./TimelineStop"
import type { DragCreateState } from "./useDragCreateStop"
import type { DragType } from "./useStopDrag"

interface TimelineRouteRowProps {
  route: Route
  isFirstStandbyRow: boolean
  hasUnsaved: boolean
  isHighlighted: boolean
  isToday: boolean
  isPastDate: boolean
  currentTime: Date
  selectedDate: Date
  hourWidth: number
  trackVariance: Variance | null
  dragPreview: DragCreateState | null
  draggingStopId: string | null
  isDragging: boolean
  onDragCreateStart: (e: React.MouseEvent) => void
  onStopDragStart: (e: React.MouseEvent, stop: Stop, route: Route, type: DragType) => void
  onStopClick: (stop: Stop, route: Route) => void
  onStopDelete: (stop: Stop, route: Route) => void
}

export default function TimelineRouteRow({
  route,
  isFirstStandbyRow,
  hasUnsaved,
  isHighlighted,
  isToday,
  isPastDate,
  currentTime,
  selectedDate,
  hourWidth,
  trackVariance,
  dragPreview,
  draggingStopId,
  isDragging,
  onDragCreateStart,
  onStopDragStart,
  onStopClick,
  onStopDelete,
}: TimelineRouteRowProps) {
  const toPosition = (time: Date) => timeToPosition(time, selectedDate, hourWidth)
  const canDragCreateStop = isToday && !route.isLocked
  // Tinted to match its label cell; no left edge, since the label cell carries it.
  const trackClasses =
    trackVariance && trackVariance !== "done" && trackVariance !== "on_plan"
      ? varianceClasses(trackVariance)
      : null
  const trackTint = trackClasses?.tint ?? null
  const trackTintHover = trackClasses?.tintHover ?? null
  // legStates[i - 1] is the leg into stop i.
  const legStates = segmentLoadStates(
    route.stops.map((s) => ({ slotType: s.slotType ?? s.type })),
    (i) => route.stops[i].volumeActual ?? route.stops[i].volumeEstimated ?? 0
  )

  return (
    <div
      className={cn(
        "relative border-b border-palette-jungleGreen100",
        trackTint ? [trackTint, trackTintHover] : "hover:bg-palette-jungleGreen000/50",
        getRouteKindLaneClasses(route.routeKind),
        isFirstStandbyRow && "border-t-2 border-t-slate-400",
        hasUnsaved && "bg-amber-50/30",
        isHighlighted && "bg-amber-100/50",
        canDragCreateStop && "cursor-crosshair"
      )}
      style={{ height: `${ROW_HEIGHT}px` }}
      onMouseDown={canDragCreateStop ? onDragCreateStart : undefined}
    >
      {dragPreview && (
        <div
          className="pointer-events-none absolute bottom-2 top-2 z-20 rounded border-2 border-dashed border-blue-400 bg-blue-100/50"
          style={{
            left: `${toPosition(dragPreview.startTime)}px`,
            width: `${toPosition(dragPreview.endTime) - toPosition(dragPreview.startTime)}px`,
            minWidth: "20px",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rounded bg-white/80 px-1 text-[10px] font-medium text-blue-600">
              {format(dragPreview.startTime, "HH:mm")} -{" "}
              {format(dragPreview.endTime, "HH:mm")}
            </span>
          </div>
        </div>
      )}
      {isPastDate && (
        <>
          {route.stops.map((stop) => (
            <PlannedStopGhost key={`planned-${stop.id}`} stop={stop} toPosition={toPosition} />
          ))}
        </>
      )}

      {route.stops.map((stop, index) => (
        <TimelineStop
          key={stop.id}
          stop={stop}
          route={route}
          index={index}
          legState={index > 0 ? legStates[index - 1] : undefined}
          isPastDate={isPastDate}
          currentTime={currentTime}
          selectedDate={selectedDate}
          hourWidth={hourWidth}
          isBeingDragged={draggingStopId === stop.id}
          isDragging={isDragging}
          routeHasUnsavedChanges={hasUnsaved}
          onDragStart={onStopDragStart}
          onClick={onStopClick}
          onDelete={onStopDelete}
        />
      ))}
    </div>
  )
}

function PlannedStopGhost({
  stop,
  toPosition,
}: {
  stop: Stop
  toPosition: (time: Date) => number
}) {
  const plannedArrivalTime = stop.plannedArrival
  const plannedDepartureTime = stop.plannedDeparture

  const actualArrivalTime = stop.actualArrival
  const actualDepartureTime = stop.actualDeparture

  if (!actualArrivalTime && !actualDepartureTime) {
    return null
  }

  const plannedStartPos = toPosition(plannedArrivalTime)
  const plannedEndPos = toPosition(plannedDepartureTime)
  const plannedWidth = Math.max(plannedEndPos - plannedStartPos, 20)

  if (actualArrivalTime) {
    const diffMinutes = Math.abs(
      (actualArrivalTime.getTime() - plannedArrivalTime.getTime()) /
        (1000 * 60)
    )
    if (diffMinutes < 5 && actualDepartureTime) {
      const depDiffMinutes = Math.abs(
        (actualDepartureTime.getTime() - plannedDepartureTime.getTime()) /
          (1000 * 60)
      )
      if (depDiffMinutes < 5) {
        return null
      }
    }
  }

  return (
    <div
      className="absolute bottom-2 top-2 z-0 rounded border border-dashed border-palette-jungleGreen300 bg-palette-jungleGreen000/30 opacity-30"
      style={{
        left: `${plannedStartPos}px`,
        width: `${plannedWidth}px`,
        minWidth: "20px",
      }}
      title={`Planned: ${format(plannedArrivalTime, "HH:mm")} - ${format(plannedDepartureTime, "HH:mm")}`}
    />
  )
}
